import { Calendar, CheckCircle2, Ticket } from "lucide-react";
import { formatCurrency, formatNumber, parseBigFloat } from "@west-tennessee-consulting/ui";
import type { Time_Entries } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact, HeaderMetric, HeaderSubtitleSegment } from "../model";
import { formatHeaderDate } from "../model";

/** Guarded against GetTimeEntries by detail-header-model-coverage.test.ts. */
export const TIME_ENTRY_HEADER_FIELDS = [
  "user_name",
  "company_id",
  "company_name",
  "hours",
  "billable_rate",
  "date_worked",
  "ticket_id",
  "ticket_number",
  "ticket_title",
  "is_approved",
  "sync_status",
] as const satisfies readonly (keyof Time_Entries)[];

export type TimeEntryHeaderData = Pick<Time_Entries, (typeof TIME_ENTRY_HEADER_FIELDS)[number]>;

/**
 * 1:1 scalar columns edited in the header (dropped from the inline panel).
 * `billable_rate` is intentionally NOT here — its config marks it read-only.
 */
export const TIME_ENTRY_HEADER_EDITABLE_FIELDS = [
  "date_worked",
  "hours",
] as const satisfies readonly (keyof Time_Entries)[];
type TimeEntryHeaderEditableField = (typeof TIME_ENTRY_HEADER_EDITABLE_FIELDS)[number];

export function buildTimeEntryHeaderModel(entry: TimeEntryHeaderData): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (entry.user_name) subtitleSegments.push({ text: entry.user_name });
  if (entry.company_name) {
    subtitleSegments.push({
      text: entry.company_name,
      href: entry.company_id ? `/companies/${entry.company_id}` : undefined,
    });
  }

  const facts: HeaderFact[] = [];
  const dateWorked = formatHeaderDate(entry.date_worked);
  if (dateWorked) {
    facts.push({
      id: "date-worked",
      icon: Calendar,
      label: "Worked",
      value: dateWorked,
      fieldId: "date_worked" satisfies TimeEntryHeaderEditableField,
    });
  }
  const ticketLabel = entry.ticket_title
    ? entry.ticket_number
      ? `#${entry.ticket_number} ${entry.ticket_title}`
      : entry.ticket_title
    : null;
  if (ticketLabel) {
    facts.push({
      id: "ticket",
      icon: Ticket,
      value: ticketLabel,
      kind: entry.ticket_id ? "internal" : "text",
      href: entry.ticket_id ? `/tickets/edit/${entry.ticket_id}` : undefined,
    });
  }
  facts.push({
    id: "approval",
    icon: CheckCircle2,
    label: "Billing",
    value: entry.is_approved
      ? entry.sync_status === "synced"
        ? "Approved · synced"
        : "Approved"
      : entry.sync_status === "disapproved"
        ? "Denied"
        : "Pending approval",
  });

  const metrics: HeaderMetric[] = [];
  const hours = formatNumber(entry.hours);
  if (hours) {
    metrics.push({
      id: "hours",
      label: "Hours",
      value: `${hours}h`,
      fieldId: "hours" satisfies TimeEntryHeaderEditableField,
    });
  }

  const rate = formatCurrency(entry.billable_rate);
  if (rate) metrics.push({ id: "rate", label: "Rate", value: rate, hint: "/hr" });

  const hoursNum = parseBigFloat(entry.hours);
  const rateNum = parseBigFloat(entry.billable_rate);
  if (hoursNum !== null && rateNum !== null) {
    const amount = formatCurrency(hoursNum * rateNum);
    if (amount) metrics.push({ id: "amount", label: "Amount", value: amount });
  }

  return { subtitleSegments, facts, metrics };
}
